import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAgent } from "@/context/AgentContext";

/**
 * IntentHandler — parses `?intent=…` query params on mount and dispatches the
 * corresponding side-effect once the user is authenticated. Designed for PWA
 * shortcuts and `share_target` deep links.
 *
 * Supported intents (frontend, browser-side):
 *   - quick_add   → opens the floating-agent dock
 *   - share_text  → forwards the `text` query param via the agent dock to
 *                   /api/ia/interpret
 *
 * Native ingestion (Siri / Tasker) does NOT use these intents — those clients
 * call /api/ingest/command directly with their integration_token. See
 * /app/INTENTS.md for the full contract.
 *
 * After dispatching, the URL is cleaned of intent params so reloading
 * doesn't re-trigger the action.
 *
 * Renders nothing.
 */
export default function IntentHandler() {
  const { user } = useAuth();
  const { openDock } = useAgent();
  const navigate = useNavigate();
  const location = useLocation();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    if (!user) return;

    const params = new URLSearchParams(location.search);
    const intent = params.get("intent");
    if (!intent) {
      handledRef.current = true;
      return;
    }
    handledRef.current = true;

    const text = params.get("text") || "";

    switch (intent) {
      case "quick_add":
        // openDock works without an anchor — positions at the floating-agent's
        // current location if mounted, otherwise default.
        openDock(null);
        break;
      case "share_text":
        if (text.trim()) {
          // Surface the text in the dock so the user can confirm/edit before
          // sending. The dock also handles its own /api/ia/interpret call.
          openDock(null);
          // Stash the text on window so AgentDock can pick it up on next mount.
          // (Lightweight cross-component handoff that doesn't bloat AgentContext.)
          try {
            window.__nxPendingShareText = text.trim();
          } catch {
            /* ignore */
          }
        }
        break;
      default:
        // Unknown intent — silently ignore but still strip params.
        break;
    }

    // Strip intent-related params from the URL without a full reload.
    params.delete("intent");
    params.delete("text");
    params.delete("title");
    params.delete("url");
    params.delete("source");
    const newSearch = params.toString();
    const target = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
    navigate(target, { replace: true });
  }, [user, location.pathname, location.search, navigate, openDock]);

  return null;
}
